import * as React from 'react';

import { Button } from '@/components/ui/button';

export interface PageErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this value (e.g. the route path) clears a previous error. */
  resetKey?: string;
}

interface PageErrorBoundaryState {
  error: Error | null;
}

/**
 * Contains render failures to the routed page so one broken screen shows a
 * recovery message instead of blanking the whole application.
 */
export class PageErrorBoundary extends React.Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  state: PageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Page render failed', error, info.componentStack);
  }

  componentDidUpdate(previous: PageErrorBoundaryProps): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="mx-auto flex w-full max-w-lg flex-col items-start gap-3 py-8">
        <h2 className="text-lg font-semibold">This page hit a problem</h2>
        <p className="text-muted-foreground text-sm">
          The rest of the application still works — move to another page from the menu, or reload
          to try again.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Button type="button" variant="outline" onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
}